// src/modules/bookLocation/keyHandover/context/SubscriptionChoiceContext.tsx

import React, { createContext, useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import {
  setHasExistingSubscription,
  type SubscriptionFlowType,
} from "../../../../redux/slice/subscription/subscriptionSlice";

type SubscriptionChoiceContextValue = {
  hasExistingSubscription: SubscriptionFlowType;
  selectExisting: () => void;
  selectNew: () => void;
  clearSelection: () => void;
};

const SubscriptionChoiceContext = createContext<
  SubscriptionChoiceContextValue | undefined
>(undefined);

export function SubscriptionChoiceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useDispatch();
  
  const hasExistingSubscription = useSelector(
    (state: RootState) => state.subscription.hasExistingSubscription
  );

  const selectExisting = useCallback(() => {
    dispatch(setHasExistingSubscription("existing"));
  }, [dispatch]);

  const selectNew = useCallback(() => {
    dispatch(setHasExistingSubscription("new"));
  }, [dispatch]);

  const clearSelection = useCallback(() => {
    dispatch(setHasExistingSubscription(null));
  }, [dispatch]);

  const value: SubscriptionChoiceContextValue = {
    hasExistingSubscription,
    selectExisting,
    selectNew,
    clearSelection,
  };

  return (
    <SubscriptionChoiceContext.Provider value={value}>
      {children}
    </SubscriptionChoiceContext.Provider>
  );
}

export function useSubscriptionChoice() {
  const context = useContext(SubscriptionChoiceContext);
  if (!context) {
    throw new Error(
      "useSubscriptionChoice must be used within SubscriptionChoiceProvider"
    );
  }
  return context;
}